import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFolder, deleteFolder, moveFolder, renameFolder } from '../api';
import { folderContentsKeys } from './useFolderContents';
import {
  type InfiniteFolderContentsData,
  prependFolderToInfiniteData,
  removeFolderFromInfiniteData,
  renameFolderInInfiniteData,
} from './folderMutationCache';

interface UseFolderMutationsOptions {
  companyId: string | null;
  currentFolderId?: string;
  search?: string;
}

export function useFolderMutations({
  companyId,
  currentFolderId,
  search,
}: UseFolderMutationsOptions) {
  const queryClient = useQueryClient();
  const infiniteQueryKey = companyId
    ? folderContentsKeys.infinite(companyId, currentFolderId, { search })
    : null;

  const createFolderMutation = useMutation({
    mutationFn: (input: { name: string; parentId: string | null }) =>
      createFolder(input),
    onSuccess: (folder) => {
      if (infiniteQueryKey) {
        queryClient.setQueryData<InfiniteFolderContentsData>(
          infiniteQueryKey,
          (data) => prependFolderToInfiniteData(data, folder, search)
        );
      }

      queryClient.invalidateQueries({ queryKey: folderContentsKeys.lists() });
    },
  });

  const renameFolderMutation = useMutation({
    mutationFn: ({ folderId, name }: { folderId: string; name: string }) =>
      renameFolder(folderId, { name }),
    onSuccess: (_, { folderId, name }) => {
      if (infiniteQueryKey) {
        queryClient.setQueryData<InfiniteFolderContentsData>(
          infiniteQueryKey,
          (data) => renameFolderInInfiniteData(data, folderId, name, search)
        );
      }

      queryClient.invalidateQueries({ queryKey: folderContentsKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: folderContentsKeys.ancestors(folderId),
      });
    },
  });

  const deleteFolderMutation = useMutation({
    mutationFn: (folderId: string) => deleteFolder(folderId),
    onSuccess: (_, folderId) => {
      if (infiniteQueryKey) {
        queryClient.setQueryData<InfiniteFolderContentsData>(
          infiniteQueryKey,
          (data) => removeFolderFromInfiniteData(data, folderId)
        );
      }

      queryClient.invalidateQueries({ queryKey: folderContentsKeys.lists() });
      queryClient.removeQueries({
        queryKey: folderContentsKeys.ancestors(folderId),
      });
    },
  });

  const moveFolderMutation = useMutation({
    mutationFn: ({
      folderId,
      newParentId,
    }: {
      folderId: string;
      newParentId: string | null;
    }) => moveFolder(folderId, { newParentId }),
    onSuccess: (_, { folderId }) => {
      if (infiniteQueryKey) {
        queryClient.setQueryData<InfiniteFolderContentsData>(
          infiniteQueryKey,
          (data) => removeFolderFromInfiniteData(data, folderId)
        );
      }

      queryClient.invalidateQueries({ queryKey: folderContentsKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: folderContentsKeys.ancestors(folderId),
      });
    },
  });

  return {
    createFolderMutation,
    renameFolderMutation,
    deleteFolderMutation,
    moveFolderMutation,
  };
}
